import React, { useEffect, useMemo, useState } from "react";

import { File as IFile, JobFolderGrouped } from "../../../models/file";
import { Folder as IFolder } from "../../../models/folder";
import { RoomJob } from "../../../models/sales";
import { FolderType } from "../../../util/enums";
import groupBy from "Util/groupBy";

import LoadingModal from "../../LoadingModal";
import ActionButtons from "../ActionButtons";
import { FilesHeader } from "./FilesHeader";
import FoldersList from "./FoldersList";
import useStyles from "./styles";

type Props = {
  onCancel?: () => void;
  onSave?: () => void;
  onSavePublish?: () => void;
  dataCyCancel?: string;
  dataCySave?: string;
  folders?: IFolder[];
  foldersDeleted?: IFolder[];
  filesDeleted?: IFile[];
  handleUpdate?: (folders?: IFolder[], foldersDeleted?: IFolder[], filesDeleted?: IFile[]) => void;
  isUpdating?: boolean;
  loading?: boolean;
  isOnViewMode?: boolean;

  inventory?: RoomJob[];

  typeFolder?: string;
  nextTypeFolder?: string;
  onSwitchTypeFolder?: () => void;

  isSendingAndSaving?: boolean;
  onSaveAndSend?: () => void;

  headerPrepend?: React.ReactNode;
  headerAppend?: React.ReactNode;
  children?: React.ReactNode;
  folderTypes?: FolderType[];
  hasAbsoluteActionButton?: boolean;
  showActionButtons?: boolean;
  compact?: boolean;
  setFolders?: React.Dispatch<React.SetStateAction<JobFolderGrouped>>;
  canAddPhoto?: boolean;
  canViewPhoto?: boolean;
  canAddDocument?: boolean;
  canViewDocument?: boolean;
};

const TYPES_TO_LIST = [FolderType.PHOTOS_VIDEOS, FolderType.DOCUMENTS];

const ALLOWED_EXTENSIONS = {
  [FolderType.PHOTOS_VIDEOS]: ["mp4", "jpg", "png", "jpeg"],
  [FolderType.DOCUMENTS]: ["pdf", "doc", "docx", "xls", "xlsx", "csv"],
};

const Files = (props: Props) => {
  const {
    dataCyCancel,
    dataCySave,
    onCancel,
    onSave,
    onSavePublish,
    folders,
    foldersDeleted,
    filesDeleted,
    handleUpdate,
    isUpdating,
    loading,
    typeFolder,
    nextTypeFolder,
    onSwitchTypeFolder,
    isOnViewMode = false,
    isSendingAndSaving = false,
    hasAbsoluteActionButton = true,
    showActionButtons = true,
    compact = false,
    onSaveAndSend,
    children,
    headerAppend,
    headerPrepend,
    folderTypes = TYPES_TO_LIST,
    canAddPhoto = true,
    canViewPhoto = true,
    canAddDocument = true,
    canViewDocument = true,
  } = props;

  const [foldersGrouped, setFoldersGrouped] = useState<JobFolderGrouped>();

  const classes = useStyles({ hasAbsoluteActionButton, compact });

  useEffect(() => {
    if (folders) setFoldersGrouped(groupBy(folders, "type"));
  }, [folders]);

  const onChangeFolders = (keyUpdate: string, folders?: IFolder[]) => {
    const foldersUpdated: IFolder[] = [];

    if (folders?.length) {
      foldersUpdated.push(...folders);
    }

    if (foldersGrouped) {
      const keys = Object.getOwnPropertyNames(foldersGrouped)?.filter((key) => key !== keyUpdate);

      if (keys?.length) {
        keys.forEach((key) => {
          if (foldersGrouped[key]) {
            foldersUpdated.push(...foldersGrouped[key]);
          }
        });
      }
    }

    if (handleUpdate) {
      handleUpdate(foldersUpdated);
    }
  };

  const onDeleteFolder = (folderDeleted?: IFolder) => {
    if (!folderDeleted?.id) return;
    const foldersDeletedUpdated: IFolder[] = [...(foldersDeleted || [])];

    foldersDeletedUpdated.push(folderDeleted);
    if (handleUpdate) handleUpdate(undefined, foldersDeletedUpdated);
  };

  const onDeleteFile = (fileDeleted?: IFolder) => {
    if (!fileDeleted?.id) return;
    const filesDeletedUpdated: IFolder[] = [...(filesDeleted || [])];

    filesDeletedUpdated.push(fileDeleted);
    if (handleUpdate) handleUpdate(undefined, undefined, filesDeletedUpdated);
  };

  const renderListsFolders = () => {
    return (
      <>
        {canViewPhoto && (
          <FoldersList
            key="photos_videos"
            allowedExtensions={ALLOWED_EXTENSIONS[FolderType.PHOTOS_VIDEOS]}
            folders={foldersGrouped?.[FolderType.PHOTOS_VIDEOS]}
            type={FolderType.PHOTOS_VIDEOS}
            onChangeFolders={(folders) => onChangeFolders(FolderType.PHOTOS_VIDEOS, folders)}
            onDeleteFolder={onDeleteFolder}
            onDeleteFile={onDeleteFile}
            loading={loading}
            isOnViewMode={isOnViewMode}
            canAdd={canAddPhoto}
          />
        )}

        {canViewDocument && (
          <FoldersList
            key="documents"
            allowedExtensions={ALLOWED_EXTENSIONS[FolderType.DOCUMENTS]}
            folders={foldersGrouped?.[FolderType.DOCUMENTS]}
            type={FolderType.DOCUMENTS}
            onChangeFolders={(folders) => onChangeFolders(FolderType.DOCUMENTS, folders)}
            onDeleteFolder={onDeleteFolder}
            onDeleteFile={onDeleteFile}
            loading={loading}
            isOnViewMode={isOnViewMode}
            canAdd={canAddDocument}
          />
        )}
      </>
    );
  };

  const renderHeader = () => {
    if (!typeFolder) return;

    return (
      <FilesHeader
        appendSlot={headerAppend}
        prependSlot={headerPrepend}
        nextTypeFolder={nextTypeFolder}
        typeFolder={typeFolder}
        onSwitchTypeFolder={onSwitchTypeFolder}
      />
    );
  };

  return (
    <div>
      <div className={isOnViewMode ? classes.containerView : classes.container}>
        {renderHeader()}
        {renderListsFolders()}

        {children}

        {!isOnViewMode && showActionButtons && (
          <div className={classes.buttons}>
            <ActionButtons
              showSaveAndSendButton={isSendingAndSaving}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              dataCyCancel={dataCyCancel}
              dataCySave={dataCySave}
              onCancel={onCancel}
              onSave={onSave}
              onSavePublish={!isUpdating ? onSavePublish : undefined}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default Files;
